// Whiteboard: one board per household-local day. Today's board is for drawing;
// earlier days are kept exactly as drawn and can be looked back through (and
// replayed), never changed. Shared by the Worker (validation, the today-only
// rule) and the screen (ink geometry, replay timing).

import { addDays } from "./dates";

/** Board units: every screen draws into this space, scaled to fit. 16:10 suits a wall beside the menu. */
export const BOARD_W = 1600;
export const BOARD_H = 1000;

export type WhiteboardTool = "ink" | "marker";
export const WHITEBOARD_TOOLS: readonly WhiteboardTool[] = ["ink", "marker"];

/** Inks chosen to read well on the warm white board, and to layer as markers. */
export const WHITEBOARD_COLORS = {
  graphite: "#2a2724",
  ember: "#c8572c",
  lake: "#2b6c9e",
  pine: "#2f7a52",
  plum: "#7a4aa0",
  sun: "#e0a400",
  rose: "#d2486a",
} as const;
export type WhiteboardColor = keyof typeof WHITEBOARD_COLORS;
export const WHITEBOARD_COLOR_NAMES: Record<WhiteboardColor, string> = {
  graphite: "Graphite",
  ember: "Ember",
  lake: "Lake",
  pine: "Pine",
  plum: "Plum",
  sun: "Sun",
  rose: "Rose",
};

/** Nib sizes in board units (a marker is drawn 3.2× wider). */
export const WHITEBOARD_SIZES = [3, 6, 12] as const;

export interface WhiteboardStroke {
  id: string;
  tool: WhiteboardTool;
  color: WhiteboardColor;
  size: number;
  /** Epoch ms when the stroke began. */
  at: number;
  /** [x, y, dt, x, y, dt, …]: board units; ms since the stroke's previous point. */
  pts: number[];
}

export interface WhiteboardDay {
  day: string;
  today: string;
  editable: boolean;
  /** The household's change counter at this answer; ask with ?since= for changes only. */
  rev: number;
  strokes: WhiteboardStroke[];
  /** With ?since=: strokes erased since then. */
  removed: string[];
}

export interface WhiteboardDays {
  today: string;
  /** Days with something on them, oldest first. */
  days: { day: string; strokes: number }[];
}

export const WHITEBOARD_LIMITS = {
  pointsPerStroke: 3000,
  strokesPerRequest: 50,
  strokesPerDay: 5000,
  idsPerRequest: 5000,
} as const;

export class WhiteboardInputError extends Error {}

/** Drawing is for today; a stroke begun just before midnight may still land a minute after. */
export function canEditDay(day: string, today: string, minutesIntoToday = 999): boolean {
  return day === today || (day === addDays(today, -1) && minutesIntoToday < 2);
}

const ID = /^[A-Za-z0-9_-]{8,40}$/;

export function isStrokeId(v: unknown): v is string {
  return typeof v === "string" && ID.test(v);
}

export function parseStroke(raw: unknown): WhiteboardStroke {
  if (!raw || typeof raw !== "object") throw new WhiteboardInputError("A stroke must be an object");
  const r = raw as Record<string, unknown>;
  if (!isStrokeId(r.id)) throw new WhiteboardInputError("Bad stroke id");
  if (!WHITEBOARD_TOOLS.includes(r.tool as WhiteboardTool)) throw new WhiteboardInputError("Unknown tool");
  if (typeof r.color !== "string" || !(r.color in WHITEBOARD_COLORS)) throw new WhiteboardInputError("Unknown color");
  if (!WHITEBOARD_SIZES.includes(r.size as (typeof WHITEBOARD_SIZES)[number])) throw new WhiteboardInputError("Unknown size");
  if (typeof r.at !== "number" || !Number.isFinite(r.at)) throw new WhiteboardInputError("Bad time");
  const pts = r.pts;
  if (!Array.isArray(pts) || pts.length < 3 || pts.length % 3 !== 0 || pts.length > WHITEBOARD_LIMITS.pointsPerStroke * 3) throw new WhiteboardInputError("Bad points");
  for (let i = 0; i < pts.length; i += 3) {
    const x = pts[i];
    const y = pts[i + 1];
    const dt = pts[i + 2];
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(dt)) throw new WhiteboardInputError("Points must be whole numbers");
    if (x < -200 || x > BOARD_W + 200 || y < -200 || y > BOARD_H + 200 || dt < 0 || dt > 600_000) throw new WhiteboardInputError("Point out of range");
  }
  return { id: r.id, tool: r.tool as WhiteboardTool, color: r.color as WhiteboardColor, size: r.size as number, at: Math.round(r.at), pts: pts as number[] };
}

// ---------- Ink geometry (pure, shared with tests) ----------

export interface InkPoint {
  x: number;
  y: number;
  /** Nib width here, in board units. */
  w: number;
}

/**
 * A fountain-pen feel without pressure hardware: the line thins as the hand speeds up,
 * thickens where it slows, and tapers in and out. Computed from the stored points and
 * timings, so every screen draws a stroke exactly the same.
 */
export function inkPoints(s: Pick<WhiteboardStroke, "pts" | "size" | "tool">): InkPoint[] {
  const n = s.pts.length / 3;
  const out: InkPoint[] = [];
  if (s.tool === "marker") {
    const w = s.size * 3.2;
    for (let i = 0; i < n; i++) out.push({ x: s.pts[i * 3]!, y: s.pts[i * 3 + 1]!, w });
    return out;
  }
  let w = s.size;
  for (let i = 0; i < n; i++) {
    const x = s.pts[i * 3]!;
    const y = s.pts[i * 3 + 1]!;
    if (i > 0) {
      const dx = x - s.pts[(i - 1) * 3]!;
      const dy = y - s.pts[(i - 1) * 3 + 1]!;
      const dt = Math.max(4, s.pts[i * 3 + 2]!);
      const speed = Math.hypot(dx, dy) / dt; // board units per ms
      const target = s.size * Math.min(1.35, Math.max(0.42, 1.3 - speed * 0.55));
      w += (target - w) * 0.35; // ink doesn't jump: ease toward the new width
    }
    out.push({ x, y, w });
  }
  // Taper the first and last few points (a nib touching down and lifting off).
  const taper = Math.min(6, Math.floor(n / 3));
  for (let i = 0; i < taper; i++) {
    const f = 0.35 + (0.65 * (i + 1)) / (taper + 1);
    out[i]!.w *= f;
    out[n - 1 - i]!.w *= f;
  }
  return out;
}

/** Hold-to-straighten: the stroke becomes a clean line from where it began to where the hand rests. */
export function straighten(pts: number[]): number[] {
  if (pts.length < 6) return pts;
  const total = pts.reduce((a, v, i) => (i % 3 === 2 ? a + v : a), 0) - pts[2]!;
  return [pts[0]!, pts[1]!, pts[2]!, pts[pts.length - 3]!, pts[pts.length - 2]!, Math.max(1, total)];
}

/** A stroke's drawn length in board units. */
export function strokeLength(pts: number[]): number {
  let len = 0;
  for (let i = 3; i < pts.length; i += 3) len += Math.hypot(pts[i]! - pts[i - 3]!, pts[i + 1]! - pts[i - 2]!);
  return len;
}

function segDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Strokes the eraser's path (board units) passes over: whole strokes go, like lifting a sticker. */
export function strokesHit(strokes: WhiteboardStroke[], path: number[], radius: number): string[] {
  const hit: string[] = [];
  for (const s of strokes) {
    const reach = radius + (s.tool === "marker" ? s.size * 1.6 : s.size * 0.7);
    let found = false;
    for (let j = 0; j < path.length && !found; j += 2) {
      const px = path[j]!;
      const py = path[j + 1]!;
      const n = s.pts.length / 3;
      for (let i = 0; i < n; i++) {
        const ax = s.pts[i * 3]!;
        const ay = s.pts[i * 3 + 1]!;
        const bx = i + 1 < n ? s.pts[(i + 1) * 3]! : ax;
        const by = i + 1 < n ? s.pts[(i + 1) * 3 + 1]! : ay;
        if (segDist(px, py, ax, ay, bx, by) <= reach) {
          found = true;
          break;
        }
      }
    }
    if (found) hit.push(s.id);
  }
  return hit;
}

/**
 * Replay as a time-lapse: strokes in the order they were drawn, each at double speed,
 * with the pauses between them shortened, so a whole day plays in seconds. Returns each
 * stroke's start and end on the replay clock (ms).
 */
export function replaySchedule(strokes: WhiteboardStroke[], maxGap = 350, speed = 2): { id: string; start: number; end: number }[] {
  const sorted = [...strokes].sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1));
  const out: { id: string; start: number; end: number }[] = [];
  let clock = 0;
  let lastEnd: number | null = null;
  for (const s of sorted) {
    let drawn = 0;
    for (let i = 5; i < s.pts.length; i += 3) drawn += s.pts[i]!;
    const length = Math.max(60, drawn / speed);
    if (lastEnd !== null) clock += Math.min(maxGap, Math.max(0, s.at - lastEnd) / speed);
    out.push({ id: s.id, start: clock, end: clock + length });
    clock += length;
    lastEnd = s.at + drawn;
  }
  return out;
}
